import type { BookingStep } from "@/types/booking";

export interface StepRequirement {
  field: string; // Key of the store's state
  // Each call site narrows `value` to the actual store field type
  // (string[], Date | null, etc.) — keep `any` here so contravariance
  // doesn't force every implementation to add a type guard.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validate: (value: any) => boolean;
  errorMessage: string;
}

export const BOOKING_STEP_REQUIREMENTS: Record<BookingStep, StepRequirement[]> =
  {
    product: [],
    // Membership cadence — entered only after a (baseline) test is selected.
    cadence: [
      {
        field: "selectedProducts",
        validate: (val: string[]) => val.length > 0,
        errorMessage: "booking.requirements.product_required",
      },
    ],
    addons: [
      {
        field: "selectedProducts",
        validate: (val: string[]) => val.length > 0,
        errorMessage: "booking.requirements.product_required",
      },
    ],
    clinic: [
      {
        field: "selectedProducts",
        validate: (val: string[]) => val.length > 0,
        errorMessage: "booking.requirements.product_required",
      },
    ],
    date: [
      {
        field: "selectedProducts",
        validate: (val: string[]) => val.length > 0,
        errorMessage: "booking.requirements.product_required",
      },
      {
        field: "selectedClinic",
        validate: (val: string | null) => val !== null,
        errorMessage: "booking.requirements.clinic_required",
      },
    ],
    time: [
      {
        field: "selectedDate",
        validate: (val: Date | null) => val !== null,
        errorMessage: "booking.requirements.date_required",
      },
    ],
    details: [
      {
        field: "selectedProducts",
        validate: (val: string[]) => val.length > 0,
        errorMessage: "booking.requirements.product_required",
      },
      {
        field: "selectedClinic",
        validate: (val: string | null) => val !== null,
        errorMessage: "booking.requirements.clinic_required",
      },
      {
        field: "selectedDate",
        validate: (val: Date | null) => val !== null,
        errorMessage: "booking.requirements.date_required",
      },
      {
        field: "selectedTimeSlots",
        validate: (val: unknown[]) => val.length > 0,
        errorMessage: "booking.requirements.time_required",
      },
    ],
    confirmation: [],
  };

// Define the linear order of steps for rollback logic.
//
// Two paths share most of the machine but diverge right after `product`:
//   • Standard booking → add-ons
//   • Membership        → cadence (frequency) selection, then the same add-ons
//                         step (members can add tests/add-ons on top of the plan)
// `product` stays in BOTH orders: it is the two-card "membership vs one-off"
// comparison page, which is where back-navigation and the order-summary edit
// pencil must land from cadence. (A ?packageType=membership entry skips PAST it
// on the way in — see initializeFromUrl — but it remains a real, reachable step.)
// Only the membership path visits `cadence`; both paths visit `addons`.
// Interactive navigation (back / history / validity) picks the active order via
// the membership-mode flag; the static `BOOKING_STEP_ORDER` below stays the
// canonical standard funnel for analytics / session-reconstruction code.
export const STANDARD_STEP_ORDER: BookingStep[] = [
  "product",
  "addons",
  "clinic",
  "date",
  "details",
  "confirmation",
];

export const MEMBERSHIP_STEP_ORDER: BookingStep[] = [
  "product",
  "cadence",
  "addons",
  "clinic",
  "date",
  "details",
  "confirmation",
];

export const BOOKING_STEP_ORDER: BookingStep[] = STANDARD_STEP_ORDER;

// The label key for each step's dot in the progress indicators. Components map it
// to their own i18n namespace (StepIndicator → `stepIndicator.*`, BookingHeader →
// `header.*`). Steps not listed (e.g. `confirmation`, `time`) get no dot.
export type IndicatorStepKey =
  | "tests"
  | "cadence"
  | "addons"
  | "clinic"
  | "date"
  | "confirm";

const STEP_INDICATOR_KEY: Partial<Record<BookingStep, IndicatorStepKey>> = {
  product: "tests",
  cadence: "cadence",
  addons: "addons",
  clinic: "clinic",
  date: "date",
  details: "confirm",
};

export interface IndicatorStep {
  step: BookingStep;
  key: IndicatorStepKey;
}

// The visible dots for BOTH progress indicators, derived from the canonical step
// order so StepIndicator, BookingHeader, and getActiveStepOrder() can never drift.
// Membership-FIRST inserts the cadence + cross-sell dots; `confirmation` (no key)
// is dropped. Callers pass `membershipFirst` — NOT `active` (an MRI-first bundle
// sets `active` but never visits those steps).
export function getIndicatorSteps(membershipFirst: boolean): IndicatorStep[] {
  const order = membershipFirst ? MEMBERSHIP_STEP_ORDER : STANDARD_STEP_ORDER;
  return order.flatMap((step) => {
    const key = STEP_INDICATOR_KEY[step];
    return key ? [{ step, key }] : [];
  });
}
